import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, BadgeCheck, Quote, MessageSquareReply } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/lib/seo";

const StarRow = ({ value, size = "w-4 h-4" }) => (
  <div className="flex gap-0.5 text-gold">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${size} ${i < value ? "fill-gold" : "fill-none text-navy/20"}`} />
    ))}
  </div>
);

export default function ReviewsPublic() {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: 0, distribution: {} });
  const [type, setType] = useState("all");
  const [ratingFilter, setRatingFilter] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = {};
    if (type !== "all") params.review_type = type;
    setLoading(true);
    Promise.all([
      api.get("/reviews", { params }),
      api.get("/reviews/summary", { params }),
    ])
      .then(([r, s]) => {
        setReviews(r.data);
        setSummary(s.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [type]);

  const visible = ratingFilter ? reviews.filter((r) => r.rating === ratingFilter) : reviews;
  const maxDist = Math.max(1, ...Object.values(summary.distribution || {}).map(Number));
  const reviewsLd = summary.count > 0 ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "JohnnyMart",
    url: "https://johnnymart.net",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: Number(summary.average).toFixed(1),
      reviewCount: summary.count,
    },
  } : null;

  return (
    <div data-testid="reviews-public-page">
      <Seo title="Customer Reviews" path="/reviews" description="Honest, moderated reviews from real JohnnyMart customers. See ratings and verified-buyer feedback before you shop." jsonLd={reviewsLd} />
      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #f2b134 0, transparent 40%), radial-gradient(circle at 80% 0, #b5231d 0, transparent 35%)" }} />
        <div className="jm-container relative py-16 lg:py-20">
          <p className="uppercase tracking-[0.2em] text-gold font-bold text-xs">Genuine Customer Reviews</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-cream">What people are saying</h1>
          <p className="mt-4 text-cream/75 max-w-2xl leading-relaxed">
            Honest, moderated feedback from real JohnnyMart customers. Reviews tied to a verified
            purchase are marked with a <span className="text-gold font-semibold">Verified Buyer</span> badge.
          </p>
          <Link to="/leave-review" data-testid="leave-review-cta"
            className="mt-6 inline-flex items-center gap-2 bg-gold text-navy font-bold uppercase rounded-md px-6 py-3.5 hover:bg-cream transition-colors">
            <Star className="w-4 h-4 fill-navy" /> Leave a Review
          </Link>
        </div>
      </section>

      <div className="jm-container py-12 grid gap-10 lg:grid-cols-[300px_1fr]">
        {/* Summary panel */}
        <aside data-testid="reviews-summary" className="bg-white rounded-xl border border-navy/15 p-6 h-fit lg:sticky lg:top-24">
          <div className="text-center">
            <div className="font-display text-6xl text-navy" data-testid="reviews-average">
              {summary.average ? summary.average.toFixed(1) : "—"}
            </div>
            <div className="mt-2 flex justify-center">
              <StarRow value={Math.round(summary.average)} size="w-5 h-5" />
            </div>
            <p className="mt-2 text-ink/60 text-sm" data-testid="reviews-count">
              Based on {summary.count} review{summary.count === 1 ? "" : "s"}
            </p>
          </div>
          <div className="mt-6 space-y-2">
            {[5, 4, 3, 2, 1].map((n) => {
              const c = Number(summary.distribution?.[n] || 0);
              return (
                <button
                  key={n}
                  data-testid={`rating-filter-${n}`}
                  onClick={() => setRatingFilter(ratingFilter === n ? 0 : n)}
                  className={`w-full flex items-center gap-2 text-sm group ${ratingFilter === n ? "font-bold" : ""}`}
                >
                  <span className="w-10 text-navy flex items-center gap-1 justify-end">{n}<Star className="w-3 h-3 fill-gold text-gold" /></span>
                  <span className="flex-1 h-2.5 rounded-full bg-navy/10 overflow-hidden">
                    <span className="block h-full bg-gold rounded-full transition-all" style={{ width: `${(c / maxDist) * 100}%` }} />
                  </span>
                  <span className="w-8 text-right text-ink/60">{c}</span>
                </button>
              );
            })}
          </div>
          {ratingFilter > 0 && (
            <button data-testid="clear-rating-filter" onClick={() => setRatingFilter(0)} className="mt-4 text-xs text-brick font-bold uppercase">Clear filter</button>
          )}
        </aside>

        {/* Reviews list */}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { k: "all", label: "All Reviews" },
              { k: "store", label: "Store Reviews" },
              { k: "product", label: "Product Reviews" },
            ].map((t) => (
              <button
                key={t.k}
                data-testid={`review-type-${t.k}`}
                onClick={() => { setType(t.k); setRatingFilter(0); }}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${type === t.k ? "bg-brick text-white" : "bg-white border border-navy/20 text-navy hover:border-brick"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="mt-10 text-navy/50">Loading reviews...</p>
          ) : visible.length === 0 ? (
            <div data-testid="reviews-empty" className="mt-12 text-center bg-white rounded-xl border border-navy/15 p-12">
              <Quote className="w-10 h-10 text-navy/20 mx-auto" />
              <p className="font-display text-2xl text-navy mt-4">No reviews yet</p>
              <p className="text-ink/60 mt-2">Be the first to share your experience after your next order.</p>
            </div>
          ) : (
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {visible.map((rev) => (
                <article key={rev.id} data-testid={`public-review-${rev.id}`} className="bg-white rounded-xl border border-navy/15 p-6 flex flex-col">
                  <div className="flex items-center justify-between gap-2">
                    <StarRow value={rev.rating} />
                    {rev.verified && (
                      <span data-testid={`verified-badge-${rev.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 bg-green-100 text-green-700">
                        <BadgeCheck className="w-3.5 h-3.5" /> Verified Buyer
                      </span>
                    )}
                  </div>
                  {rev.title && <h3 className="mt-3 font-bold text-navy">{rev.title}</h3>}
                  <p className="mt-2 text-ink/80 leading-relaxed flex-1">"{rev.text}"</p>
                  {rev.review_type === "product" && rev.product_name && (
                    <Link to={rev.product_slug ? `/product/${rev.product_slug}` : "#"} className="mt-3 text-xs font-bold text-brick uppercase hover:underline">
                      On: {rev.product_name}
                    </Link>
                  )}
                  {rev.owner_response && (
                    <div className="mt-4 rounded-lg bg-cream border border-gold/40 p-3">
                      <p className="flex items-center gap-1.5 text-xs font-bold text-navy uppercase">
                        <MessageSquareReply className="w-3.5 h-3.5 text-brick" /> JohnnyMart replied
                      </p>
                      <p className="mt-1 text-sm text-ink/80">{rev.owner_response}</p>
                    </div>
                  )}
                  <div className="mt-4 flex items-center justify-between text-xs text-ink/50">
                    <span className="font-bold text-navy not-italic">{rev.author}</span>
                    <span>
                      {rev.location ? `${rev.location} · ` : ""}
                      {rev.created_at ? new Date(rev.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : ""}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
